import type { ChartDropPlace } from '../../model/chartDrop';

/** Прямоугольник на странице в координатах окна. */
type PageRect = Pick<DOMRectReadOnly, 'left' | 'top' | 'right' | 'bottom' | 'width' | 'height'>;

/** Ячейка диаграммы сетки и её место на странице. */
type ChartDropCell = {
  chartId: string;
  rect: PageRect;
};

/** Строка сетки глазами перетаскивания: её ячейки по порядку и незанятый конец в колонках раскладки. */
export type ChartDropRow = {
  cells: ChartDropCell[];
  freeColumns: number;
};

/** Раскладка сетки на момент перетаскивания: из неё и выводится место под курсором. */
export type ChartDropLayout = {
  gridRect: PageRect;
  rows: ChartDropRow[];
  /** Перетаскиваемая диаграмма: её ячейка на прежнем месте и занятые ею колонки раскладки */
  source: ChartDropCell & { columns: number };
  /** Отступ карточки от краёв её ячейки — половина зазора между диаграммами по ширине и по высоте */
  cellGutter: { x: number; y: number };
};

/**
 * Индикатор места в координатах сетки: черта между диаграммами строки, черта между строками либо
 * контур диаграммы на пустом месте строки.
 */
export type ChartDropIndicatorShape =
  | { kind: 'vertical'; left: number; top: number; height: number }
  | { kind: 'horizontal'; left: number; top: number; width: number }
  | { kind: 'outline'; left: number; top: number; width: number; height: number };

/** Место под курсором: куда встанет диаграмма и чем это место показано. */
export type ChartDropTarget = {
  place: ChartDropPlace;
  indicator: ChartDropIndicatorShape;
};

/**
 * Запас, на который промежуток между строками заходит в сами строки: попасть в промежуток не
 * должно требовать точности, а ширины видимого зазора для этого мало.
 */
const ROW_GAP_CATCH = 8;

/** Вертикальные границы строки — по самой высокой из её диаграмм. */
const rowBounds = (row: ChartDropRow) => ({
  top: Math.min(...row.cells.map((cell) => cell.rect.top)),
  bottom: Math.max(...row.cells.map((cell) => cell.rect.bottom)),
});

/**
 * Место перетаскиваемой диаграммы под курсором `pointer` и его индикатор; курсор вне сетки или над
 * самой перетаскиваемой диаграммой — `null`.
 *
 * Промежуток между строками перекрывает края строк запасом и берёт всё, что между ними, — в том числе
 * развёрнутые под строкой свойства диаграммы. Внутри строки место выбирается по ячейке под курсором:
 * у ближнего к курсору края диаграммы, а правее последней — на пустом месте строки. Ячейка включает
 * свою половину зазора, поэтому промежуток между диаграммами строки ловится с запасом сам собой.
 *
 * Пустое место строки показано контуром перетаскиваемой диаграммы там, где она встанет: следом за
 * диаграммами строки, а если ей не хватает ширины — в начале следующей строки. Сама она в счёт ширины
 * строки не идёт: её место освобождается переносом.
 */
export const chartDropTarget = (pointer: { x: number; y: number }, layout: ChartDropLayout): ChartDropTarget | null => {
  const { gridRect, rows, source, cellGutter } = layout;
  if (pointer.x < gridRect.left || pointer.x > gridRect.right) return null;
  if (pointer.y < gridRect.top || pointer.y > gridRect.bottom + ROW_GAP_CATCH) return null;

  const toGridX = (x: number) => x - gridRect.left;
  const toGridY = (y: number) => y - gridRect.top;

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const bounds = rowBounds(row);
    const nextRow = rows[rowIndex + 1];
    const nextTop = nextRow ? rowBounds(nextRow).top : null;
    const others = row.cells.filter((cell) => cell.chartId !== source.chartId);
    const lastOther = others.at(-1);

    if (pointer.y < bounds.bottom - ROW_GAP_CATCH) {
      const cell = row.cells.find((item) => pointer.x < item.rect.right);
      if (cell) {
        if (cell.chartId === source.chartId) return null;
        const isBefore = pointer.x < cell.rect.left + cell.rect.width / 2;
        return {
          place: isBefore ? { kind: 'before', chartId: cell.chartId } : { kind: 'after', chartId: cell.chartId },
          indicator: {
            kind: 'vertical',
            left: toGridX(isBefore ? cell.rect.left : cell.rect.right),
            top: toGridY(bounds.top),
            height: bounds.bottom - bounds.top,
          },
        };
      }

      // Пустое место правее последней диаграммы строки
      if (!lastOther) return null;
      const isSourceInRow = others.length < row.cells.length;
      const freeColumns = row.freeColumns + (isSourceInRow ? source.columns : 0);
      const isFitting = freeColumns >= source.columns;
      const othersWidth = others.reduce((sum, item) => sum + item.rect.width, 0);
      return {
        place: { kind: 'after', chartId: lastOther.chartId },
        indicator: {
          kind: 'outline',
          left: (isFitting ? othersWidth : 0) + cellGutter.x,
          top: isFitting ? toGridY(bounds.top) : toGridY(nextTop ?? bounds.bottom + cellGutter.y * 2),
          width: source.rect.width - cellGutter.x * 2,
          height: source.rect.height,
        },
      };
    }

    if (nextTop === null || pointer.y <= nextTop + ROW_GAP_CATCH) {
      if (!lastOther) return null;
      // Новая строка встаёт сразу над следующей — ниже всего, что развёрнуто под этой строкой
      const lineY = nextTop === null ? bounds.bottom + cellGutter.y : nextTop - cellGutter.y;
      return {
        place: { kind: 'newRow', afterChartId: lastOther.chartId },
        indicator: {
          kind: 'horizontal',
          left: cellGutter.x,
          top: toGridY(lineY),
          width: gridRect.width - cellGutter.x * 2,
        },
      };
    }
  }

  return null;
};
